import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { readExpiredFlag, clearExpiredFlag } from "@/lib/activity";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { PreferencesCollapsible } from "@/components/PreferencesCollapsible";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { welcomeToast } from "@/lib/welcome";
import { Loader2 } from "lucide-react";
import { safeNext, stashNext, homeFor } from "@/lib/nextPath";

const NAME_MAX = 50, PASS_MIN = 6, PASS_MAX = 12, LOGIN_PASS_MAX = 20;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function Login({ mode }) {
  const isRegister = mode === "register";
  const { login, register, user, setUser } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const roleParam = params.get("rol") || params.get("role");
  const next = safeNext(params.get("next"));
  const defaultRole = (roleParam === "maestro" || roleParam === "business") ? "business" : "customer";

  const [form, setForm] = useState({ email: params.get("email") || "", password: "", name: "", phone: "", role: defaultRole, preferences: [] });
  const isMaestroReg = isRegister;
  const emailOk = EMAIL_RE.test(form.email);
  const passOk = form.password.length >= PASS_MIN && form.password.length <= PASS_MAX;
  const showEmailErr = form.email.length > 0 && !emailOk;
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [otpStep, setOtpStep] = useState(false);
  const [otpEmail, setOtpEmail] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [expired] = useState(() => readExpiredFlag() || !!params.get("expired"));
  useEffect(() => { clearExpiredFlag(); }, []);
  useEffect(() => { if (mode === "login") window.scrollTo(0, 0); }, [mode]);

  useEffect(() => { api.get("/categories").then((r) => setCategories(r.data)); }, []);
  useEffect(() => {
    if (user) {
      navigate(homeFor(user, next));
    }
  }, [user, navigate, next]);

  useEffect(() => {
    if (!otpStep || secondsLeft <= 0) return;
    const t = setInterval(() => setSecondsLeft((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [otpStep, secondsLeft]);

  const togglePref = (c) => setForm((f) => ({
    ...f, preferences: f.preferences.includes(c) ? f.preferences.filter((x) => x !== c) : [...f.preferences, c],
  }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isRegister) {
        if (isMaestroReg) {
          if (!form.name.trim()) { toast.error(form.role === "business" ? "Ingresa tu nombre de Maestro / marca" : "Ingresa tu nombre"); return; }
          if (!emailOk) { toast.error("Ingresa un correo válido con la forma nombre@dominio.cl"); return; }
          if (!passOk) { toast.error(`La contraseña debe tener entre ${PASS_MIN} y ${PASS_MAX} caracteres`); return; }
        }
        const { data } = await api.post("/auth/register/start", form);
        if (data?.onboarding_pending) {
          setUser(data.user);
          toast.success(data.message);
          navigate("/maestro");
        } else {
          setOtpEmail(form.email); setOtpCode(""); setOtpStep(true); setSecondsLeft(300);
          toast.success("Te enviamos un código de 6 dígitos a tu correo");
        }
      } else {
        if (!emailOk) { toast.error("Ingresa un correo válido con la forma nombre@dominio.cl"); return; }
        if (!form.password) { toast.error("Ingresa tu contraseña"); return; }
        const u = await login(form.email, form.password);
        welcomeToast(u);
        navigate(homeFor(u, next));
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  const verifyOtp = async () => {
    if (otpCode.length !== 6) { toast.error("Ingresa los 6 dígitos"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register/verify", { email: otpEmail, code: otpCode });
      setUser(data);
      toast.success("¡Cuenta verificada!");
      navigate(homeFor(data, next));
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const resendOtp = async () => {
    try {
      await api.post("/auth/register/resend", { email: otpEmail });
      setSecondsLeft(300); setOtpCode(""); toast.success("Código reenviado");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const googleLogin = () => {
    stashNext(next);
    const redirectUrl = window.location.origin + "/maestro";
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] grid place-items-center px-4 py-10">
      {otpStep && (
        <Card className="w-full max-w-sm p-6 text-center" data-testid="otp-card">
          <img src="/vivotaller-logo.webp" alt="VIVOTaller" className="w-14 h-14 rounded-2xl object-cover mb-3 mx-auto shadow-sm" />
          <h1 className="text-xl font-extrabold">Verifica tu correo</h1>
          <p className="text-sm text-muted-foreground mt-1">Ingresa el código de 6 dígitos que enviamos a <strong>{otpEmail}</strong></p>
          <Input value={otpCode} onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            inputMode="numeric" maxLength={6} data-testid="otp-input"
            className="mt-5 text-center text-2xl tracking-[0.5em] font-mono h-14" placeholder="------" />
          <p className="text-xs text-muted-foreground mt-2">
            {secondsLeft > 0 ? `El código vence en ${String(Math.floor(secondsLeft / 60))}:${String(secondsLeft % 60).padStart(2, "0")}` : "El código expiró, solicita uno nuevo"}
          </p>
          <Button className="w-full h-11 mt-4" onClick={verifyOtp} disabled={loading || otpCode.length !== 6} data-testid="otp-verify">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Verificar y crear cuenta"}
          </Button>
          <div className="flex items-center justify-between mt-3 text-sm">
            <button type="button" className="text-muted-foreground hover:text-foreground" onClick={() => setOtpStep(false)} data-testid="otp-back">← Volver</button>
            <button type="button" className="text-primary font-medium disabled:opacity-50" onClick={resendOtp} disabled={secondsLeft > 240} data-testid="otp-resend">Reenviar código</button>
          </div>
        </Card>
      )}
      {!otpStep && (
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <img src="/vivotaller-logo.webp" alt="VIVOTaller" className="w-14 h-14 rounded-2xl object-cover mb-3 mx-auto shadow-sm" />
          <h1 className="text-2xl font-extrabold tracking-tight">
            {isRegister ? "Crea tu cuenta" : "Ingresa a VIVOTaller"}
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            {!isRegister ? "Escribe tu correo y contraseña, o continúa con Google" : defaultRole === "business" ? "Publica y gestiona tus talleres" : "Gestiona tus reservas y talleres"}
          </p>
        </div>

        {!isRegister && expired && (
          <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/30 px-3 py-2.5 text-sm text-amber-900 dark:text-amber-200" data-testid="session-expired-msg">
            Tu sesión se cerró por seguridad. Ingresa nuevamente para continuar.
          </div>
        )}
        <Card className="p-6">
          <form onSubmit={submit} className="space-y-4">
            {isRegister && (
              <>
                <div className="grid grid-cols-2 gap-2">
                  <button type="button" onClick={() => setForm({ ...form, role: "customer" })}
                    className={`rounded-xl border p-3 text-sm font-medium transition ${form.role === "customer" ? "border-primary bg-primary/5 ring-1 ring-primary" : "border-border"}`}
                    data-testid="role-customer">Soy Alumno</button>
                  <button type="button" onClick={() => setForm({ ...form, role: "business" })}
                    className={`rounded-xl border p-3 text-sm font-medium transition ${form.role === "business" ? "border-primary bg-primary/5 ring-1 ring-primary" : "border-border"}`}
                    data-testid="role-business">Soy Maestro</button>
                </div>
                <div>
                  <Label htmlFor="name">{form.role === "business" ? "Nombre de Maestro / marca" : "Nombre"}</Label>
                  <Input id="name" value={form.name} maxLength={isMaestroReg ? NAME_MAX : undefined}
                    onChange={(e) => setForm({ ...form, name: isMaestroReg ? e.target.value.slice(0, NAME_MAX) : e.target.value })}
                    required data-testid="register-name"
                    placeholder={form.role === "business" ? "Tu marca o tu nombre de Maestro" : "Tu nombre"} />
                  {isMaestroReg && (
                    <p className={`text-xs mt-1 text-right tabular-nums ${form.name.length >= NAME_MAX ? "text-destructive" : "text-muted-foreground"}`} data-testid="register-name-counter">
                      {form.name.length} / {NAME_MAX}
                    </p>
                  )}
                </div>
              </>
            )}
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={form.email} required data-testid="auth-email" aria-invalid={showEmailErr}
                onChange={(e) => setForm({ ...form, email: e.target.value.trim() })} />
              {showEmailErr && (
                <p className="text-xs text-destructive mt-1" data-testid="register-email-error">Ingresa un correo válido con la forma nombre@dominio.cl</p>
              )}
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Contraseña</Label>
                {!isRegister && (
                  <Link to={`/forgot-password${form.email ? `?email=${encodeURIComponent(form.email.trim())}` : ""}`} className="text-xs text-primary hover:underline" data-testid="forgot-link">
                    ¿Olvidaste tu contraseña?
                  </Link>
                )}
              </div>
              <PasswordInput id="password" value={form.password} required data-testid="auth-password"
                disabled={!emailOk} maxLength={isMaestroReg ? PASS_MAX : LOGIN_PASS_MAX}
                placeholder={!emailOk ? "Primero ingresa un correo válido" : undefined}
                onChange={(e) => setForm({ ...form, password: e.target.value.slice(0, isMaestroReg ? PASS_MAX : LOGIN_PASS_MAX) })} />
              {isMaestroReg && (
                <p className={`text-xs mt-1 ${form.password && !passOk ? "text-destructive" : "text-muted-foreground"}`} data-testid="register-password-hint">
                  Entre {PASS_MIN} y {PASS_MAX} caracteres{form.password ? ` · ${form.password.length} / ${PASS_MAX}` : ""}
                </p>
              )}
            </div>
            {isRegister && form.role === "customer" && (
              <PreferencesCollapsible title="Tus talleres favoritos" categories={categories} selected={form.preferences} onToggle={togglePref}
                testId="register-preferences" hint="Te mostraremos ofertas relacionadas al entrar. Podrás ver todos igualmente." />
            )}
            <Button type="submit" className="w-full h-11" disabled={loading} data-testid="auth-submit">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : isRegister ? "Crear cuenta" : "Ingresar"}
            </Button>
          </form>

          <div className="relative my-5 text-center">
            <span className="relative z-10 bg-card px-3 text-xs text-muted-foreground">o continúa con</span>
            <div className="absolute inset-0 top-1/2 border-t border-border" />
          </div>
          <Button variant="outline" className="w-full h-11 gap-2" onClick={googleLogin} data-testid="google-login">
            <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="w-5 h-5" />
            Google
          </Button>
        </Card>

        <p className="text-center text-sm text-muted-foreground mt-5">
          {isRegister ? (
            <>¿Ya tienes cuenta? <Link to="/login" className="text-primary font-medium hover:underline">Ingresa</Link></>
          ) : (
            <>¿No tienes cuenta? <Link to="/registro" className="text-primary font-medium hover:underline" data-testid="go-register">Regístrate</Link></>
          )}
        </p>
      </div>
      )}
    </div>
  );
}
